import {
  Configuration,
  CreateUserRoleDto,
  FindUserRolesDto,
  UpdateUserRoleDto,
  UserRole,
  UserRolesApi,
} from '@sr-sdks/api-sdk-axios';
import {
  QueryClient,
  queryOptions,
  skipToken,
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type { RawAxiosRequestConfig } from 'axios';
import { configuration } from '../../configuration';
import axiosInstance, {
  bypassCacheConfig,
  invalidateCacheRequestConfig,
} from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';
import { USER_ME_AXIOS_CACHE_KEY, invalidateUserQueries } from './userApi';

/**
 * Query key factory for user roles
 */
export const userRoleQueryKeys = {
  all: ['userRoles'] as const,
  list: (params: FindUserRolesDto) =>
    [...userRoleQueryKeys.lists(), params] as const,
  lists: () => [...userRoleQueryKeys.all, 'list'] as const,
};

export const userRoleMutationKeys = {
  create: () => [...userRoleQueryKeys.all, 'create'] as const,
  remove: () => [...userRoleQueryKeys.all, 'remove'] as const,
  update: () => [...userRoleQueryKeys.all, 'update'] as const,
};

const GetUserRolesApi = () => {
  return new UserRolesApi(
    new Configuration(),
    configuration.PERMISSIONS_API_BASE,
    axiosInstance,
  );
};

export interface UpdateUserRoleParams {
  id: string;
  updateUserRoleDto: UpdateUserRoleDto;
}

export const fetchUserRoles = async (
  { permissionKey, userId, workspaceId }: FindUserRolesDto,
  options?: RawAxiosRequestConfig,
): Promise<UserRole[]> => {
  try {
    const response = await GetUserRolesApi().userRolesV2ControllerFindAll(
      permissionKey,
      userId,
      workspaceId,
      options,
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'userRolesV2ControllerFindAll',
      params: { permissionKey, userId, workspaceId },
    });
  }
};

// The axios layer caches GETs on top of react-query, and the Redux user slice
// reads through it (`queryOptionsGetUserMe` skips it). Each write evicts the
// entries it makes stale, so the next Redux fetch is not served from before
// the change.
export const createUserRole = async (
  createUserRoleDto: CreateUserRoleDto,
): Promise<UserRole> => {
  try {
    const response = await GetUserRolesApi().userRolesV2ControllerCreate(
      createUserRoleDto,
      invalidateCacheRequestConfig(
        `v3/user?workspaceId=${createUserRoleDto.workspaceId}`,
        USER_ME_AXIOS_CACHE_KEY,
      ),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'userRolesV2ControllerCreate',
      params: { createUserRoleDto },
    });
  }
};

export const removeUserRole = async (id: string): Promise<UserRole> => {
  try {
    const response = await GetUserRolesApi().userRolesV2ControllerRemove(
      id,
      invalidateCacheRequestConfig(
        'v3/user?workspaceId',
        USER_ME_AXIOS_CACHE_KEY,
      ),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'userRolesV2ControllerRemove',
      params: { id },
    });
  }
};

export const updateUserRole = async ({
  id,
  updateUserRoleDto,
}: UpdateUserRoleParams): Promise<UserRole> => {
  try {
    const response = await GetUserRolesApi().userRolesV2ControllerUpdate(
      id,
      updateUserRoleDto,
      invalidateCacheRequestConfig(USER_ME_AXIOS_CACHE_KEY),
    );

    return response.data;
  } catch (error) {
    throw toApiError(error, {
      endpoint: 'userRolesV2ControllerUpdate',
      params: { id, updateUserRoleDto },
    });
  }
};

export const queryOptionsFindUserRoles = (params?: FindUserRolesDto) => {
  return queryOptions({
    queryFn: params
      ? () => fetchUserRoles(params, bypassCacheConfig())
      : skipToken,
    queryKey: userRoleQueryKeys.list(params ?? {}),
  });
};

/**
 * A role write changes the role lists and what `GET /user/me` and the
 * workspace user lists report in `roles`, so all three are marked stale.
 * Exported for callers that write through the plain functions above.
 */
export const invalidateUserRoleQueries = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: userRoleQueryKeys.all }),
    invalidateUserQueries(queryClient),
  ]);

export const useCreateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createUserRole,
    mutationKey: userRoleMutationKeys.create(),
    // Not returned, so `mutateAsync` resolves on the write: a table firing
    // several in parallel does not wait on one refetch round per call.
    onSuccess: () => {
      invalidateUserRoleQueries(queryClient);
    },
  });
};

export const useRemoveUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeUserRole,
    mutationKey: userRoleMutationKeys.remove(),
    // Not returned, so `mutateAsync` resolves on the write: a table firing
    // several in parallel does not wait on one refetch round per call.
    onSuccess: () => {
      invalidateUserRoleQueries(queryClient);
    },
  });
};

/**
 * Whether any role create or remove is in flight. Counts every call, where a
 * hook's own `isPending` follows only the latest of the parallel ones a table
 * fires.
 */
export const useUserRoleMutationState = () => ({
  isCreating: useIsMutating({ mutationKey: userRoleMutationKeys.create() }) > 0,
  isRemoving: useIsMutating({ mutationKey: userRoleMutationKeys.remove() }) > 0,
});

export const useUpdateUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateUserRole,
    mutationKey: userRoleMutationKeys.update(),
    // Not returned, so `mutateAsync` resolves on the write: a table firing
    // several in parallel does not wait on one refetch round per call.
    onSuccess: () => {
      invalidateUserRoleQueries(queryClient);
    },
  });
};
